import { IProduct } from '@models/IProduct';
import { ListingProductType } from '@models/IProductListing';
import { IProductListingStatusLabels } from '@models/IProductListingStatusLabels';
import { IPromoCard } from '@models/IPromoCard';
import ListingCardTypeEnum from '@models/enums/ListingCardTypeEnum';
import KitDropdown from '@molecules/KitDropdown/KitDropdown';
import ProductListingCard from '@molecules/ProductListingCard/ProductListingCard';
import PromoCard from '@molecules/PromoCard/PromoCard';
import useFirstFocusable from '@utils/hooks/useFirstFocusable';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import useRefocus from '@utils/hooks/useRefocus';
import useResizeObserver from '@utils/hooks/useResizeObserver';
import useTabInteractionCheck from '@utils/hooks/useTabInteractionCheck';
import { AnimatePresence, motion, useInView } from 'framer-motion';
import { MutableRefObject, useEffect, useRef, useState } from 'react';

interface Props {
  readonly activeCard: IProduct | null;
  readonly activeColumnIndex: number | null;
  readonly activeRowIndex: number | null;
  readonly chunkedResults: ListingProductType[][];
  readonly nextFocusIndex: number;
  readonly products: ListingProductType[];
  readonly productListingName?: string;
  readonly rowIndex: number;
  readonly statusLabels: IProductListingStatusLabels;
  readonly onDesktopKitDropdownButtonClick: (
    rowIndex: number,
    columnIndex: number
  ) => void;
  readonly onMobileKitDropdownButtonClick: (rowIndex: number) => void;
  readonly onListingProductsTracking: (products: ListingProductType[]) => void;
  readonly onCloseDropdown: () => void;
}

const ProductListingCardRow = ({
  activeCard,
  activeColumnIndex,
  activeRowIndex,
  chunkedResults,
  nextFocusIndex,
  products,
  productListingName,
  rowIndex,
  statusLabels,
  onDesktopKitDropdownButtonClick,
  onMobileKitDropdownButtonClick,
  onListingProductsTracking,
  onCloseDropdown,
}: Props) => {
  const rowRef = useRef<HTMLDivElement | null>(null);
  const [kitDropdownLeftOffset, setKitDropdownLeftOffset] = useState(0);
  const [kitDropdownWidth, setKitDropdownWidth] = useState(0);
  const [kitDropdownRefCallback, { height: kitDropdownHeight }] =
    useResizeObserver();
  const kitDropdownRef = useRef<HTMLDivElement>(null);
  const spacerRef = useRef<HTMLDivElement | null>(null);
  const activeColumnRef = useRef<HTMLDivElement | null>(null);
  const placeholderRef = useRefocus(chunkedResults);
  const isTabInteraction = useTabInteractionCheck();
  const getFirstFocusableElement = useFirstFocusable();
  const isMobile = useMediaQuery(1024);
  const isRowInView = useInView(rowRef, { amount: 0.5 });
  const dropdownIsActive = rowIndex === activeRowIndex && !!activeCard;

  const handleKitDropdownUpdate = () => {
    if (!activeColumnRef?.current) return;

    const rect = activeColumnRef.current.getBoundingClientRect();
    const { scrollX } = window;
    setKitDropdownLeftOffset(rect.left + scrollX);
    setKitDropdownWidth(document.documentElement.clientWidth);
  };

  useEffect(() => {
    if (isRowInView) onListingProductsTracking(products);
  }, [isRowInView, onListingProductsTracking, products]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional Additional Dependencies
  useEffect(() => {
    if (activeRowIndex === rowIndex) {
      handleKitDropdownUpdate();
    }
  }, [activeColumnIndex, activeRowIndex, rowIndex]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional Additional Dependencies
  useEffect(() => {
    window.addEventListener('resize', handleKitDropdownUpdate);
    handleKitDropdownUpdate();

    return () => {
      window.removeEventListener('resize', handleKitDropdownUpdate);
    };
  }, []);

  const handleDropdownBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    const relatedTarget = e.relatedTarget as HTMLElement | null;

    if (
      isTabInteraction &&
      kitDropdownRef.current &&
      relatedTarget &&
      !kitDropdownRef.current.contains(relatedTarget)
    ) {
      onCloseDropdown();
    }
  };

  return (
    <div ref={rowRef} className="relative overflow-hidden">
      <div className="container grid gap-5 bg-black-100 lg:grid-cols-12">
        {products.map((product, columnIndex) => {
          const isActiveColumn =
            activeColumnIndex === columnIndex &&
            activeRowIndex === rowIndex &&
            !!activeCard;

          return (
            <div
              tabIndex={-1}
              ref={(el) => {
                if (isActiveColumn) activeColumnRef.current = el;
                if (
                  nextFocusIndex > 0 &&
                  nextFocusIndex === 3 * rowIndex + columnIndex
                ) {
                  (
                    placeholderRef as MutableRefObject<HTMLDivElement | null>
                  ).current = el;
                }
              }}
              key={product.title}
              className="relative col-span-full overflow-visible lg:col-span-4"
            >
              {product.type === ListingCardTypeEnum.Product && (
                <ProductListingCard
                  {...product}
                  statusLabels={statusLabels}
                  onKitDropdownButtonClick={() =>
                    isMobile
                      ? onMobileKitDropdownButtonClick(rowIndex)
                      : onDesktopKitDropdownButtonClick(rowIndex, columnIndex)
                  }
                  active={isActiveColumn}
                  productListingName={productListingName}
                />
              )}
              {product.type === ListingCardTypeEnum.Promo && (
                <PromoCard {...(product as IPromoCard)} />
              )}

              {isActiveColumn && kitDropdownWidth && (
                <>
                  {/* Hidden dropdown to measure height */}
                  <div
                    className="-z-10 pointer-events-none invisible absolute top-full opacity-0"
                    tabIndex={-1}
                    ref={kitDropdownRefCallback}
                    aria-hidden="true"
                    style={{
                      left: `-${kitDropdownLeftOffset}px`,
                      width: `${kitDropdownWidth}px`,
                      overflowX: 'clip',
                    }}
                  >
                    <KitDropdown
                      productListingName={productListingName}
                      statusLabels={statusLabels}
                      kitsTitleLabel={activeCard?.kitsTitleLabel}
                      kitTitleLabel={activeCard?.kitTitleLabel}
                      variations={activeCard?.variations ?? []}
                    />
                  </div>

                  {/* Visible dropdown */}
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={`${activeCard?.title}-${rowIndex}-${activeColumnIndex}`}
                      ref={kitDropdownRef}
                      tabIndex={-1}
                      onBlur={handleDropdownBlur}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{
                        height: kitDropdownHeight,
                        opacity: 1,
                      }}
                      exit={{ height: 0, opacity: 0 }}
                      className="absolute top-full z-100"
                      style={{
                        left: `-${kitDropdownLeftOffset}px`,
                        width: `${kitDropdownWidth}px`,
                        maxWidth: `${kitDropdownWidth}px`,
                        overflowX: 'clip',
                      }}
                    >
                      <KitDropdown
                        productListingName={productListingName}
                        statusLabels={statusLabels}
                        kitsTitleLabel={activeCard.kitsTitleLabel}
                        kitTitleLabel={activeCard.kitTitleLabel}
                        variations={activeCard.variations}
                      />
                    </motion.div>
                  </AnimatePresence>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Spacer to prevent layout shift */}
      <AnimatePresence mode="wait">
        {dropdownIsActive && (
          <motion.div
            key={`spacer-${activeRowIndex}`}
            initial={{ height: 0 }}
            animate={{ height: kitDropdownHeight }}
            exit={{ height: 0 }}
            transition={{ duration: 0.25 }}
            style={{ overflow: 'hidden' }}
            aria-hidden="true"
            ref={spacerRef}
            onAnimationComplete={({ height }: { height: number }) => {
              if (height > 0 && height === kitDropdownHeight) {
                requestAnimationFrame(() =>
                  spacerRef.current?.scrollIntoView({ behavior: 'smooth' })
                );

                // Focus first element in dropdown after giving time for the transition to complete
                setTimeout(() => {
                  getFirstFocusableElement(kitDropdownRef.current)?.focus({
                    preventScroll: true,
                  });
                }, 300);
              }
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProductListingCardRow;
